import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../api/api";
import {
  COMMENTS_PAGE_SIZE,
  formatForumDateTime,
  getForumTitle,
  normalizeJobForumComment,
  normalizeJobForumLike,
  normalizeJobForumPost,
  saveForumState,
} from "./jobReferralStore";

function buildJobForumCommentPayload(postId, currentUserId, commentText) {
  return {
    JobForumPostId: Number(postId),
    CommentedBy: currentUserId,
    CommentText: commentText.trim(),
    IsActive: true,
  };
}

function JobReferralPostDetails() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const userState = useSelector((state) => state.user);
  const currentUser = userState?.user || {};
  const [forumState, setForumState] = useState({
    posts: [],
    comments: [],
    likes: [],
  });
  const [commentText, setCommentText] = useState("");
  const [commentPage, setCommentPage] = useState(1);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [isLoadingForum, setIsLoadingForum] = useState(true);

  const currentUserId =
    Number(currentUser.userId || currentUser.id || currentUser.Id) || 9999;
  const currentUserName =
    `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() ||
    "Current Alumni";
  const currentUserYear = currentUser.passedOutYear || "Batch not set";
  const currentUserToken =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";

  useEffect(() => {
    saveForumState(forumState);
  }, [forumState]);

  useEffect(() => {
    let isMounted = true;

    async function fetchForumData() {
      try {
        setIsLoadingForum(true);
        const [postsResponse, commentsResponse, likesResponse] = await Promise.all([
          api.getJobForumPosts(currentUserToken),
          api.getJobForumComments(currentUserToken),
          api.getJobForumLikes(currentUserToken),
        ]);

        const rawPosts = Array.isArray(postsResponse?.data)
          ? postsResponse.data
          : postsResponse?.data?.posts || postsResponse?.data?.data || [];
        const rawComments = Array.isArray(commentsResponse?.data)
          ? commentsResponse.data
          : commentsResponse?.data?.comments || commentsResponse?.data?.data || [];
        const rawLikes = Array.isArray(likesResponse?.data)
          ? likesResponse.data
          : likesResponse?.data?.likes || likesResponse?.data?.data || [];

        const likes = rawLikes.map(normalizeJobForumLike);
        const posts = rawPosts.map((post) => {
          const normalizedPost = normalizeJobForumPost(post);
          return {
            ...normalizedPost,
            likedBy: likes
              .filter((like) => Number(like.postId) === Number(normalizedPost.id))
              .map((like) => like.likedBy),
          };
        });
        const comments = rawComments.map(normalizeJobForumComment);

        if (!isMounted) {
          return;
        }

        setForumState({ posts, comments, likes });
      } catch (error) {
        console.error("Failed to load forum data", error);
        if (isMounted) {
          setForumState({ posts: [], comments: [], likes: [] });
        }
      } finally {
        if (isMounted) {
          setIsLoadingForum(false);
        }
      }
    }

    fetchForumData();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken]);

  const post = useMemo(
    () =>
      forumState.posts.find((item) => Number(item.id) === Number(postId)) ||
      null,
    [forumState.posts, postId],
  );

  const postComments = useMemo(() => {
    return forumState.comments
      .filter((comment) => Number(comment.postId) === Number(postId))
      .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
  }, [forumState.comments, postId]);

  const totalPages = Math.max(
    1,
    Math.ceil(postComments.length / COMMENTS_PAGE_SIZE),
  );
  const pagedComments = postComments.slice(
    (commentPage - 1) * COMMENTS_PAGE_SIZE,
    commentPage * COMMENTS_PAGE_SIZE,
  );

  useEffect(() => {
    setCommentPage(1);
  }, [postComments.length]);

  if (isLoadingForum) {
    return (
      <Box sx={{ bgcolor: "#f5f8ff", py: 6 }}>
        <Container maxWidth="md">
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ textAlign: "center", py: 6 }}>
              <Typography color="text.secondary">
                Loading forum post...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box sx={{ bgcolor: "#f5f8ff", py: 6 }}>
        <Container maxWidth="md">
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                Post not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                The forum post may have been deleted.
              </Typography>
              <Button component={RouterLink} to="/job-referral" variant="contained">
                Back to Forum
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const isPostOwner = Number(post.postedBy) === currentUserId;

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      return;
    }

    try {
      setIsSubmittingComment(true);
      const requestBody = buildJobForumCommentPayload(
        post.id,
        currentUserId,
        commentText,
      );
      const response = await api.createJobForumComment(
        requestBody,
        currentUserToken,
      );
      const responseData = response?.data ?? {};
      const now = new Date().toISOString();

      setForumState((prev) => ({
        ...prev,
        comments: [
          ...prev.comments,
          {
            id: responseData.id ?? responseData.commentId ?? Date.now(),
            postId: post.id,
            postedBy: currentUserId,
            posterName: currentUserName,
            passedOutYear: currentUserYear,
            content: requestBody.CommentText,
            createdDate: responseData.createdDate ?? responseData.CreatedDate ?? now,
            updatedDate: responseData.updatedDate ?? responseData.UpdatedDate ?? now,
            isActive: true,
          },
        ],
      }));
      setCommentText("");
    } catch (error) {
      console.error("Failed to save comment", error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleUpdateComment = async () => {
    if (!editingText.trim()) {
      return;
    }

    try {
      setIsUpdatingComment(true);
      const response = await api.updateJobForumComment(
        editingCommentId,
        {
          CommentText: editingText.trim(),
          IsActive: true,
        },
        currentUserToken,
      );
      const responseData = response?.data ?? {};

      setForumState((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.id === editingCommentId
            ? {
                ...comment,
                content: editingText.trim(),
                updatedDate:
                  responseData.updatedDate ??
                  responseData.UpdatedDate ??
                  new Date().toISOString(),
                isActive: true,
              }
            : comment,
        ),
      }));
      setEditingCommentId(null);
      setEditingText("");
    } catch (error) {
      console.error("Failed to update comment", error);
    } finally {
      setIsUpdatingComment(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    setForumState((prev) => ({
      ...prev,
      comments: prev.comments.filter((comment) => comment.id !== commentId),
    }));
  };

  return (
    <Box sx={{ bgcolor: "#f5f8ff", py: { xs: 4, md: 7 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/job-referral")}
            sx={{ width: "fit-content", textTransform: "none" }}
          >
            Back to Forum
          </Button>

          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}>
                    {post.posterName?.charAt(0) || "A"}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={800} color="#0f172a">
                      {getForumTitle(post)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {post.forumType} • Posted by {post.posterName} •{" "}
                      {post.passedOutYear} • {formatForumDateTime(post.createdDate)}
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                  {post.jobDescription}
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Job Title
                    </Typography>
                    <Typography fontWeight={700}>
                      {post.jobTitle || "Not provided"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Forum Type
                    </Typography>
                    <Typography fontWeight={700}>{post.forumType}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    {post.forumType === "Job Referral" ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Company
                        </Typography>
                        <Typography fontWeight={700}>
                          {post.companyName}
                        </Typography>
                      </>
                    ) : null}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Experience
                    </Typography>
                    <Typography fontWeight={700}>{post.experience} years</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography fontWeight={700}>{post.locationCity}</Typography>
                  </Grid>
                </Grid>

                <Divider />

                <Typography variant="body2" color="text.secondary">
                  Contact Number: {post.contactNumber || "Not shared"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                    Comments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comments stay on this page only. The main forum shows counts,
                    not the full discussion.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Add Comment"
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                />
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleAddComment}
                    disabled={isSubmittingComment}
                  >
                    Post Comment
                  </Button>
                </Box>

                <Stack spacing={2}>
                  {pagedComments.map((comment) => {
                    const canManage =
                      Number(comment.postedBy) === currentUserId || isPostOwner;

                    return (
                      <Card
                        key={comment.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          borderColor: "rgba(148, 163, 184, 0.28)",
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar sx={{ width: 36, height: 36, bgcolor: "#e2e8f0" }}>
                                  {comment.posterName?.charAt(0) || "A"}
                                </Avatar>
                                <Box>
                                  <Typography fontWeight={700}>
                                    {comment.posterName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {comment.passedOutYear} •{" "}
                                    {formatForumDateTime(comment.updatedDate)}
                                  </Typography>
                                </Box>
                              </Stack>
                              {canManage ? (
                                <Stack direction="row" spacing={0.5}>
                                  {Number(comment.postedBy) === currentUserId ? (
                                    <Tooltip title="Edit comment">
                                      <IconButton
                                        onClick={() => {
                                          setEditingCommentId(comment.id);
                                          setEditingText(comment.content);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                  <Tooltip title="Delete comment">
                                    <IconButton
                                      onClick={() => handleDeleteComment(comment.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              ) : null}
                            </Stack>
                            <Typography sx={{ color: "#334155", lineHeight: 1.8 }}>
                              {comment.content}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Pagination
                    page={commentPage}
                    count={totalPages}
                    color="primary"
                    onChange={(_, value) => setCommentPage(value)}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>

      <Dialog
        open={Boolean(editingCommentId)}
        onClose={() => {
          setEditingCommentId(null);
          setEditingText("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Comment</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={editingText}
            onChange={(event) => setEditingText(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingCommentId(null);
              setEditingText("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateComment}
            disabled={isUpdatingComment}
          >
            Update Comment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default JobReferralPostDetails;
